// apps/client/src/components/XeroSyncButton.tsx
// Generic Xero sync button with progress tracking

import { useState, useEffect, useRef } from 'react';
import { Button, Typography, CircularProgress, Stack } from '@mui/material';
import {
  Sync as SyncIcon,
  Security as SecurityIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useXeroSync } from '../../../contexts/QueueContext';
import { dispatchXeroSync } from '../../../services/xeroSync';
import { classifyXeroSyncError } from '../../../utils/xeroSyncError';
import { XeroAPIErrorCode, XeroSyncType, XeroSyncStatus, JobStatus } from '@avanzu/types';

interface XeroSyncButtonProps<TResponse> {
  syncType: XeroSyncType;
  onSyncComplete?: (result: TResponse) => void;
  variant?: 'contained' | 'outlined' | 'text';
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  disabled?: boolean;
  startDate?: Date;
  endDate?: Date;
  pollInterval?: number; // milliseconds, default 250
}

export function XeroSyncButton<
  TResponse extends {
    success: boolean;
    message?: string;
    status: XeroSyncStatus;
    processed?: number;
    created?: number;
    updated?: number;
    skipped?: number;
    deleted?: number;
  },
>({
  syncType,
  onSyncComplete,
  variant = 'contained',
  size = 'medium',
  fullWidth = false,
  disabled = false,
  startDate,
  endDate,
  pollInterval = 250,
}: XeroSyncButtonProps<TResponse>) {
  const { organisationId, currentOrganisation } = useAuth();
  const { startSync, currentJob } = useXeroSync();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<XeroAPIErrorCode | null>(null);
  const [showConnectButton, setShowConnectButton] = useState(false);
  const jobIdRef = useRef<string | null>(null);

  const handleSync = async () => {
    if (!organisationId) {
      setErrorMessage('No organisation selected');
      return;
    }

    // Validate date range for syncs that require it
    if (
      (syncType === XeroSyncType.PAYSLIPS ||
        syncType === XeroSyncType.TRANSACTIONS ||
        syncType === XeroSyncType.BILLS) &&
      (!startDate || !endDate)
    ) {
      setErrorMessage('Start date and end date are required');
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);
    setErrorCode(null);
    setShowConnectButton(false);

    try {
      // Dispatch sync job
      const dispatchResult = await dispatchXeroSync(organisationId, {
        syncType,
        startDate: startDate?.toISOString().split('T')[0],
        endDate: endDate?.toISOString().split('T')[0],
      });

      if (!dispatchResult.success) {
        setErrorMessage(dispatchResult.message || 'Failed to start sync');
        setIsLoading(false);
        return;
      }

      const jobId = dispatchResult.jobId;
      jobIdRef.current = jobId;

      // Start the global sync status with polling
      startSync({
        jobId,
        syncType,
        progress: 0,
        total: 0,
        status: JobStatus.QUEUED,
        organisationId,
        pollInterval,
      });
    } catch (err) {
      console.error(`Error syncing Xero ${syncType}:`, err);
      setErrorMessage(`Failed to sync ${syncType}.`);
      setIsLoading(false);
    }
  };

  const handleSyncError = (code?: XeroAPIErrorCode, message?: string) => {
    setErrorCode(code || null);

    switch (code) {
      case XeroAPIErrorCode.INSUFFICIENT_PERMISSIONS:
        setErrorMessage("You don't have enough Xero permissions");
        break;
      case XeroAPIErrorCode.INVALID_CREDENTIALS:
      case XeroAPIErrorCode.TOKEN_EXPIRED:
      case XeroAPIErrorCode.MISSING_TENANT_ID:
        setShowConnectButton(true);
        setErrorMessage(message || 'Xero connection expired');
        break;
      case XeroAPIErrorCode.API_ERROR:
        setShowConnectButton(true);
        setErrorMessage('Xero connection expired');
        break;
      case XeroAPIErrorCode.RATE_LIMIT_EXCEEDED:
        setErrorMessage('Xero API rate limit — try again in a minute');
        break;
      default: {
        const info = classifyXeroSyncError(message);
        setErrorMessage(info.display);
        break;
      }
    }
  };

  // Monitor job completion and call onSyncComplete callback
  useEffect(() => {
    if (!currentJob || !jobIdRef.current || currentJob.jobId !== jobIdRef.current) {
      return;
    }

    if (currentJob.status === JobStatus.COMPLETED) {
      setIsLoading(false);

      // Build success response
      const removedNote = currentJob.deleted
        ? `, removed ${currentJob.deleted} deleted in Xero`
        : '';
      const successResponse = {
        success: true,
        message: `Successfully synced ${currentJob.total} ${syncType}${removedNote}`,
        status: XeroSyncStatus.COMPLETE,
        processed: currentJob.total || 0,
        created: 0,
        updated: 0,
        skipped: 0,
        deleted: currentJob.deleted || 0,
      } as TResponse;

      onSyncComplete?.(successResponse);
      jobIdRef.current = null;
    } else if (currentJob.status === JobStatus.FAILED) {
      setIsLoading(false);
      const raw = currentJob.errorMessage || 'Sync failed';
      handleSyncError(undefined, raw);

      // Rate-limit failures usually mean we persisted some rows before the
      // 429 hit. Refresh so the UI reflects what *did* land.
      if (classifyXeroSyncError(raw).isRateLimit) {
        onSyncComplete?.({
          success: false,
          message: raw,
          status: XeroSyncStatus.ERROR,
          processed: currentJob.progress || 0,
          created: 0,
          updated: 0,
          skipped: 0,
        } as TResponse);
      }

      jobIdRef.current = null;
    }
  }, [currentJob, syncType, onSyncComplete]);

  const connected = currentOrganisation?.connectedToXero && !showConnectButton;

  // Check if there's any sync currently running (disable button if so)
  const isSyncRunning =
    currentJob &&
    (currentJob.status === JobStatus.QUEUED ||
      currentJob.status === JobStatus.IN_PROGRESS);

  // Special UI for insufficient permissions (mainly for assets)
  if (errorCode === XeroAPIErrorCode.INSUFFICIENT_PERMISSIONS) {
    return (
      <Stack direction="column" alignItems="flex-end" spacing={1}>
        <Button
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          disabled={true}
          startIcon={<SecurityIcon sx={{ color: 'error.main' }} />}
          sx={{
            color: 'error.main',
            borderColor: 'error.main',
            '&.Mui-disabled': {
              color: 'error.main',
              borderColor: 'error.main',
            },
          }}
        >
          Insufficient Permissions
        </Button>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
      {errorMessage && !isLoading && !isSyncRunning && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <ErrorIcon sx={{ fontSize: 16, color: 'error.main' }} />
          <Typography variant="caption" color="error.main">
            {errorMessage}
          </Typography>
        </Stack>
      )}
      {!connected ? (
        <Button variant="contained" size="small" component={RouterLink} to="/settings/xero">
          Connect to Xero
        </Button>
      ) : (
        <Button
          variant={variant}
          size={size}
          fullWidth={fullWidth}
          disabled={disabled || isLoading || !!isSyncRunning}
          onClick={handleSync}
          startIcon={isLoading || isSyncRunning ? <CircularProgress size={16} /> : <SyncIcon />}
          sx={{
            position: 'relative',
            ...((isLoading || isSyncRunning) && {
              '& .MuiButton-startIcon': {
                marginLeft: 0,
                marginRight: 1,
              },
            }),
          }}
        >
          {(() => {
            // The 'transactions' job pulls both bank transactions and bills
            // in a single run, so phrase the label to reflect that.
            const label =
              syncType === XeroSyncType.TRANSACTIONS ? 'transactions & bills' : syncType;
            return isLoading ? `Syncing ${label}...` : `Sync Xero ${label}`;
          })()}
        </Button>
      )}
    </Stack>
  );
}

export default XeroSyncButton;
